import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import { FiUsers } from "react-icons/fi";
import {
  OrganizationType,
  SourcingEventStatus,
  useGetOrganizationQuery,
  useGetSourcingEventQuery,
  useRemoveEmailInviteFromPrivateSourcingEventMutation,
  useRemoveOrganizationInviteFromPrivateSourcingEventMutation,
  type EmailInviteDto,
  type MinimalOrgDto,
  type OrganizationInvitePatchDto,
  type OrganizationInviteDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../../common/auth/useLoggedInWithOrgContextState";
import { useEditor } from "../../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../../common/errors/useApiError";
import { orgRemovalText } from "../../../../../sourcing-event/inviteRemovalText";
import { OrganizationSelector } from "../../../../../../common/organization/OrganizationSelector";
import { OrganizationTypeTag } from "../../../../../../common/organization/OrganizationTypeTag";
import { StepNavigation } from "../../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../../common/useConfirmationModal";
import { CreateOrganizationEntryModal } from "../../../../../organizations/entry/create-entry/CreateOrganizationEntryModal";
import { isBeforeBidDeadline, useHasPassed } from "../../../../../sourcing-event/bidDeadlineWindow";
import { InviteOrganizationEntryModal } from "../../../../../sourcing-event/InviteOrganizationEntryModal";
import { InviteOrganizationModal, type OrganizationInviteRecipients } from "./InviteOrganizationModal";
import { EmailInviteAccountTag } from "./EmailInviteAccountTag";
import { registeredMemberInvite, type RegisteredMember } from "./RegisteredAccountHint";
import { SectionCard } from "../../../../portal/view-single/SectionCard";
import { OrganizationNameCell } from "../../../../../../common/organizations/OrganizationNameCell";
import { EntryRemovalFailureAlert, useEntryRemoval } from "../../../../../sourcing-event/useEntryRemoval";
import { companyContaining, groupByCompany } from "./companyInvites";

interface RecipientsAdded {
  organizationId: string;
  recipients: OrganizationInviteRecipients;
}

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const InvitationsStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(
    eventId,
  );
  const { data: serverEvent } = useGetSourcingEventQuery({ eventId });
  const [removeEmailInvite, { isLoading: isRemovingEmailInvite }] =
    useRemoveEmailInviteFromPrivateSourcingEventMutation();
  const [removeOrgInviteFromPublishedEvent] = useRemoveOrganizationInviteFromPrivateSourcingEventMutation();
  const confirmationModal = useConfirmationModal();
  const isPublished = state.status === SourcingEventStatus.Published;
  const remindsOfCopiedInvitations =
    state.status === SourcingEventStatus.Draft && !!(serverEvent ?? state).hasCopiedInvitations;
  const bidDeadline = serverEvent?.eventData.bidSubmissionDeadline;
  const deadlineHasPassed = useHasPassed(isPublished ? bidDeadline : undefined);
  const [closedForDeadline, setClosedForDeadline] = useState<string | null | undefined>(undefined);
  const invitingClosed = closedForDeadline !== undefined && closedForDeadline === (bidDeadline ?? null);
  const setInvitingClosed = () => setClosedForDeadline(bidDeadline ?? null);
  const invitingOpen = !isPublished || (isBeforeBidDeadline(bidDeadline) && !deadlineHasPassed && !invitingClosed);

  const [selectedOrgEntry, setSelectedOrgEntry] = useState<MinimalOrgDto | undefined>();
  const [selectedOrgForRecipients, setSelectedOrgForRecipients] = useState<MinimalOrgDto | undefined>();
  const [inviteToAddRecipientsTo, setInviteToAddRecipientsTo] = useState<
    { organization: MinimalOrgDto; companyInvites: OrganizationInviteDto[] } | undefined
  >();
  const [showCreateEntryModal, setShowCreateEntryModal] = useState(false);

  const orgInvites = state.eventContext.type === "Private" ? state.eventContext.organizationInvites : [];
  const emailInvites: EmailInviteDto[] =
    serverEvent?.eventContext.type === "Private" ? serverEvent.eventContext.emailInvites ?? [] : [];

  const emailInvitesByEntry = useMemo(() => {
    const groups = new Map<string, EmailInviteDto[]>();
    emailInvites.forEach((invite) => {
      const entryId = invite.organizationEntry?.id ?? "";
      groups.set(entryId, [...(groups.get(entryId) ?? []), invite]);
    });
    return [...groups.values()];
  }, [emailInvites]);

  const invitedOrgIds = useMemo(() => orgInvites.map((invite) => invite.organizationId), [orgInvites]);
  const sameLegalEntityOf = useMemo(() => {
    const byOrganization = new Map(
      orgInvites.map((invite) => [invite.organizationId, invite.sameLegalEntityIds ?? [invite.organizationId]]),
    );
    return (organizationId: string) => byOrganization.get(organizationId) ?? [organizationId];
  }, [orgInvites]);
  const companies = groupByCompany(orgInvites, sameLegalEntityOf);
  const orgsToExclude = useMemo(
    () => [
      selectedOrg.id,
      ...emailInvites.map((invite) => invite.organizationEntry?.id).filter((id): id is string => !!id),
    ],
    [selectedOrg.id, emailInvites],
  );

  const toPatchInvite = (invite: OrganizationInviteDto): OrganizationInvitePatchDto => ({
    organizationId: invite.organizationId,
    persons: (invite.persons ?? []).map((person) => ({ id: person.id })),
    emailInvites: (invite.emailInvites ?? []).map((email) => ({ email: email.email, language: email.language })),
  });

  const saveOrgInvites = (invites: OrganizationInvitePatchDto[]) => {
    updateField({ privateContext: { organizationInvites: invites } });
  };

  const unconfirmedAdditions = useRef<RecipientsAdded[]>([]);
  const unconfirmedOrganizations = useRef<string[]>([]);
  const [notAdded, setNotAdded] = useState<RecipientsAdded[]>([]);
  const [organizationsNotAdded, setOrganizationsNotAdded] = useState<string[]>([]);
  const invitesSaveStatus = fieldStatus(ORGANIZATION_INVITES_PATH);
  const invitesRefusedAfterDeadline = fieldError(ORGANIZATION_INVITES_PATH)?.code === INVITING_CLOSED;
  useEffect(() => {
    if (invitesSaveStatus === "saved") {
      unconfirmedAdditions.current = [];
      unconfirmedOrganizations.current = [];
      return;
    }
    if (invitesSaveStatus !== "error") return;
    if (unconfirmedAdditions.current.length === 0 && unconfirmedOrganizations.current.length === 0) return;
    const refused = unconfirmedAdditions.current;
    const refusedOrganizations = unconfirmedOrganizations.current;
    unconfirmedAdditions.current = [];
    unconfirmedOrganizations.current = [];
    if (invitesRefusedAfterDeadline) setInvitingClosed();
    setNotAdded((previous) => [...previous, ...refused]);
    setOrganizationsNotAdded((previous) => [...previous, ...refusedOrganizations.filter((id) => !previous.includes(id))]);
    saveOrgInvites(
      orgInvites
        .filter((invite) => !refusedOrganizations.includes(invite.organizationId))
        .map(toPatchInvite)
        .map((invite) => withoutRecipients(invite, refused)),
    );
  });

  const addOrgInvite = (organizationId: string, recipients: OrganizationInviteRecipients) => {
    if (invitedOrgIds.includes(organizationId)) return;
    unconfirmedOrganizations.current = [...unconfirmedOrganizations.current, organizationId];
    setOrganizationsNotAdded((previous) => previous.filter((id) => id !== organizationId));
    saveOrgInvites([
      ...orgInvites.map(toPatchInvite),
      {
        organizationId,
        persons: recipients.personIds.map((id) => ({ id })),
        emailInvites: recipients.emailInvites,
      },
    ]);
  };

  const addRecipients = (organizationId: string, recipients: OrganizationInviteRecipients) => {
    unconfirmedAdditions.current = [...unconfirmedAdditions.current, { organizationId, recipients }];
    setNotAdded((previous) => previous.filter((addition) => addition.organizationId !== organizationId));
    saveOrgInvites(
      orgInvites.map((invite) => {
        const current = toPatchInvite(invite);
        if (invite.organizationId !== organizationId) return current;
        return {
          ...current,
          persons: [...(current.persons ?? []), ...recipients.personIds.map((id) => ({ id }))],
          emailInvites: [...(current.emailInvites ?? []), ...recipients.emailInvites],
        };
      }),
    );
  };

  const inviteRegisteredMember = (member: RegisteredMember) => {
    const recipients = { personIds: [member.personId], emailInvites: [] };
    const invite = registeredMemberInvite(orgInvites, member);
    if (invite === "newInvite") addOrgInvite(member.organization.id, recipients);
    if (invite === "addToInvite") addRecipients(member.organization.id, recipients);
  };

  const removeOrgInvite = async (organizationIds: string[]) => {
    if (!isPublished) {
      saveOrgInvites(orgInvites.filter((invite) => !organizationIds.includes(invite.organizationId)).map(toPatchInvite));
      return;
    }
    try {
      await removeOrgInviteFromPublishedEvent({ eventId, pathOrganizationId: organizationIds[0] }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  const confirmRemoveOrgInvite = (organizationIds: string[], organizationName?: string) => {
    if (!isPublished) {
      removeOrgInvite(organizationIds);
      return;
    }
    confirmationModal.show({
      title: t("Confirm removal"),
      description: orgRemovalText(organizationName, true, t),
      onConfirmed: () => removeOrgInvite(organizationIds),
      confirmButtonColor: "red",
      confirmButtonText: t("Remove") ?? "",
    });
  };

  const selectOrganization = (organization?: MinimalOrgDto) => {
    if (!organization) return;
    if (organization.type === OrganizationType.Organization) {
      const companyInvites = companyContaining(companies, organization.id, sameLegalEntityOf);
      if (companyInvites) setInviteToAddRecipientsTo({ organization, companyInvites });
      else setSelectedOrgForRecipients(organization);
    } else {
      setSelectedOrgEntry(organization);
    }
  };

  const handleRemoveEmailInvite = async (inviteId: string) => {
    try {
      await removeEmailInvite({ eventId, inviteId }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  const entryRemoval = useEntryRemoval((inviteId) => removeEmailInvite({ eventId, inviteId }).unwrap());

  const confirmRemoveEntry = (entryKey: string, entryName: string, inviteIds: string[]) => {
    const remove = () => entryRemoval.removeEntry(entryKey, entryName, inviteIds);
    if (!isPublished) {
      void remove();
      return;
    }
    confirmationModal.show({
      title: t("Confirm removal"),
      description: orgRemovalText(entryName, true, t),
      onConfirmed: remove,
      confirmButtonColor: "red",
      confirmButtonText: t("Remove") ?? "",
    });
  };

  if (state.eventContext.type !== "Private") {
    return <Text pt="10">{t("Invitations apply to private procurements only.")}</Text>;
  }

  return (
    <Box pb="8" pt="10">
      {showCreateEntryModal && (
        <CreateOrganizationEntryModal
          onConfirm={async (newEntry) => {
            setSelectedOrgEntry({ type: OrganizationType.OrganizationEntry, ...newEntry });
          }}
          onSelectExisting={(org) => selectOrganization(org)}
          onClose={() => setShowCreateEntryModal(false)}
        />
      )}
      <InviteOrganizationEntryModal
        eventId={eventId}
        onInviteRegisteredMember={inviteRegisteredMember}
        minimalOrgEntry={selectedOrgEntry}
        alreadyInvitedEmails={emailInvites.map((invite) => invite.email)}
        onClose={() => setSelectedOrgEntry(undefined)}
        onInvitingClosed={setInvitingClosed}
      />
      <InviteOrganizationModal
        eventId={eventId}
        minimalOrg={selectedOrgForRecipients}
        onInviteRegisteredMember={inviteRegisteredMember}
        onClose={() => setSelectedOrgForRecipients(undefined)}
        onCreate={(recipients) => {
          if (selectedOrgForRecipients) addOrgInvite(selectedOrgForRecipients.id, recipients);
        }}
      />
      <InviteOrganizationModal
        eventId={eventId}
        minimalOrg={inviteToAddRecipientsTo?.organization}
        onInviteRegisteredMember={inviteRegisteredMember}
        alreadyInvited={inviteToAddRecipientsTo && everyoneInvited(inviteToAddRecipientsTo.companyInvites)}
        confirmButtonText={t("Add people") ?? undefined}
        onClose={() => setInviteToAddRecipientsTo(undefined)}
        onCreate={(recipients) => {
          if (inviteToAddRecipientsTo) {
            addRecipients(inviteToAddRecipientsTo.companyInvites[0].organizationId, recipients);
          }
        }}
      />

      <SectionCard icon={FiUsers} title={t("Invited organizations")} count={companies.length + emailInvites.length}>
        {remindsOfCopiedInvitations && (
          <Alert status="warning" borderRadius="md" fontSize="sm" mb="5">
            <AlertIcon />
            {t("The invitations from the original were copied. Review them before you publish.")}
          </Alert>
        )}
        {invitingOpen && (
          <Box pb="5">
            <Text color="smMuted" fontSize="sm" pb="2">
              {t("Invite organizations to this private procurement.")}
            </Text>
            <OrganizationSelector
              organization={selectedOrgForRecipients ?? selectedOrgEntry}
              orgsToExclude={orgsToExclude}
              onChange={({ organization }) => selectOrganization(organization)}
            />
            <Text fontSize="sm" color="smMuted" pt="1">
              {t("Choose a registered company, or")}{" "}
              <Button
                variant="link"
                colorScheme="blue"
                fontWeight="bold"
                fontSize="sm"
                onClick={() => setShowCreateEntryModal(true)}
              >
                {t("create a new company entry")}
              </Button>
            </Text>
          </Box>
        )}
        {entryRemoval.failure && (
          <EntryRemovalFailureAlert
            failure={entryRemoval.failure}
            isRetrying={entryRemoval.removingKey === entryRemoval.failure.entryKey}
            onRetry={entryRemoval.retry}
          />
        )}
        <Box border="1px solid" borderColor="smBorder" rounded="md" overflow="hidden">
          <TableContainer whiteSpace="normal">
            <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>{t("Organization name")}</Th>
                <Th>{t("Type")}</Th>
                <Th>{t("Recipients")}</Th>
                <Th textAlign="right" whiteSpace="nowrap">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {companies.map((companyInvites) => (
                <InvitedOrgRow
                  key={companyInvites[0].organizationId}
                  eventId={eventId}
                  companyInvites={companyInvites}
                  canAddPeople={invitingOpen}
                  notAdded={notAdded.filter((addition) =>
                    companyContaining([companyInvites], addition.organizationId, sameLegalEntityOf),
                  )}
                  deadlineHasPassed={invitingClosed || (isPublished && !isBeforeBidDeadline(bidDeadline))}
                  onAddRecipients={(organization) => setInviteToAddRecipientsTo({ organization, companyInvites })}
                  onRemove={(organizationName) =>
                    confirmRemoveOrgInvite(
                      companyInvites.map((companyInvite) => companyInvite.organizationId),
                      organizationName,
                    )
                  }
                />
              ))}
              {organizationsNotAdded.map((organizationId) => (
                <NotAddedOrgRow
                  key={organizationId}
                  organizationId={organizationId}
                  deadlineHasPassed={invitingClosed || (isPublished && !isBeforeBidDeadline(bidDeadline))}
                />
              ))}
              {emailInvitesByEntry.map((invites) => {
                const entryKey = invites[0].organizationEntry?.id ?? "no_org_entry";
                const isRemovingEntry = entryRemoval.removingKey === entryKey;
                return (
                <Tr key={entryKey}>
                  <Td verticalAlign="top">
                    <Text fontSize="sm">{invites[0].organizationEntry?.name ?? t("No organization")}</Text>
                  </Td>
                  <Td verticalAlign="top">
                    <OrganizationTypeTag type={OrganizationType.OrganizationEntry} />
                  </Td>
                  <Td verticalAlign="top">
                    {invites.map((invite) => (
                      <Flex key={invite.id} align="center" gap="1" pb="1">
                        <Text fontSize="sm" overflowWrap="anywhere">
                          {invite.email}
                        </Text>
                        <IconButton
                          aria-label={t("Remove {{email}}", { email: invite.email })}
                          icon={<Icon as={FaTimes} />}
                          size="xs"
                          variant="ghost"
                          isDisabled={isRemovingEmailInvite || isRemovingEntry}
                          onClick={() => handleRemoveEmailInvite(invite.id)}
                        />
                      </Flex>
                    ))}
                  </Td>
                  <Td verticalAlign="top" textAlign="right" whiteSpace="nowrap">
                    <Flex justify="flex-end" align="center" gap="2">
                      {invitingOpen && invites[0].organizationEntry && (
                        <Button
                          variant="solid"
                          size="xs"
                          colorScheme="blue"
                          onClick={() => {
                            const entry = invites[0].organizationEntry;
                            if (entry) setSelectedOrgEntry({ type: OrganizationType.OrganizationEntry, ...entry });
                          }}
                        >
                          {t("Add people")}
                        </Button>
                      )}
                      {invites[0].organizationEntry && (
                        <IconButton
                          aria-label={t("Remove {{organization}}", { organization: invites[0].organizationEntry.name })}
                          icon={<Icon as={FaTimes} />}
                          size="sm"
                          variant="ghost"
                          isLoading={isRemovingEntry}
                          isDisabled={isRemovingEmailInvite || isRemovingEntry}
                          onClick={() => {
                            const entry = invites[0].organizationEntry;
                            if (entry) confirmRemoveEntry(entryKey, entry.name, invites.map((invite) => invite.id));
                          }}
                        />
                      )}
                    </Flex>
                  </Td>
                </Tr>
                );
              })}
              {companies.length + emailInvites.length === 0 && (
                <Tr>
                  <Td colSpan={4} textAlign="center" color="smMuted">
                    {t("No invites have been added")}
                  </Td>
                </Tr>
              )}
            </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </SectionCard>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
      {confirmationModal.modal}
    </Box>
  );
};

const everyoneInvited = (companyInvites: OrganizationInviteDto[]) => {
  const persons = companyInvites.flatMap((invite) => invite.persons ?? []);
  return {
    personIds: persons.map((person) => person.id),
    emails: [
      ...persons.map((person) => person.email).filter((email): email is string => !!email),
      ...companyInvites.flatMap((invite) => invite.emailInvites ?? []).map((invite) => invite.email),
    ],
  };
};

const InvitedOrgRow = ({
  eventId,
  companyInvites,
  canAddPeople,
  notAdded,
  deadlineHasPassed,
  onAddRecipients,
  onRemove,
}: {
  eventId: string;
  companyInvites: OrganizationInviteDto[];
  canAddPeople: boolean;
  notAdded: RecipientsAdded[];
  deadlineHasPassed: boolean;
  onAddRecipients: (organization: MinimalOrgDto) => void;
  onRemove: (organizationName: string | undefined) => void;
}) => {
  const { t } = useTranslation();
  const orgQuery = useGetOrganizationQuery({ orgId: companyInvites[0].organizationId });
  const org = orgQuery.data;
  const persons = companyInvites.flatMap((companyInvite) => companyInvite.persons ?? []);
  const emailInvites = companyInvites.flatMap((companyInvite) => companyInvite.emailInvites ?? []);

  return (
    <Tr>
      <Td verticalAlign="top">
        <OrganizationNameCell query={orgQuery} />
      </Td>
      <Td verticalAlign="top">
        <OrganizationTypeTag type={OrganizationType.Organization} />
      </Td>
      <Td verticalAlign="top">
        {persons.map((person) => (
          <Text key={person.id} fontSize="sm">
            {`${person.firstName} ${person.lastName} (${person.email})`.trim()}
          </Text>
        ))}
        {emailInvites.map((email) => (
          <Text key={email.email} fontSize="sm" pt="1">
            {email.email} <EmailInviteAccountTag eventId={eventId} email={email.email} canLookUp />
          </Text>
        ))}
        {notAdded.length > 0 && <NotAddedRecipients additions={notAdded} deadlineHasPassed={deadlineHasPassed} />}
        {!persons.length && !emailInvites.length && !notAdded.length && (
          <Text fontSize="sm" color="smMuted">
            –
          </Text>
        )}
      </Td>
      <Td verticalAlign="top" textAlign="right" whiteSpace="nowrap">
        <Flex justify="flex-end" align="center" gap="2">
          {org && canAddPeople && (
            <Button
              variant="solid"
              size="xs"
              colorScheme="blue"
              onClick={() =>
                onAddRecipients({
                  type: OrganizationType.Organization,
                  id: org.id,
                  name: org.name,
                  organizationNumber: org.organizationNumber,
                  country: org.country,
                })
              }
            >
              {t("Add people")}
            </Button>
          )}
          <IconButton
            aria-label={t("Remove") ?? "Remove"}
            icon={<Icon as={FaTimes} />}
            size="sm"
            variant="ghost"
            onClick={() => onRemove(org?.name)}
          />
        </Flex>
      </Td>
    </Tr>
  );
};

const NotAddedOrgRow = ({ organizationId, deadlineHasPassed }: { organizationId: string; deadlineHasPassed: boolean }) => {
  const { t } = useTranslation();
  const orgQuery = useGetOrganizationQuery({ orgId: organizationId });

  return (
    <Tr>
      <Td verticalAlign="top">
        <OrganizationNameCell query={orgQuery} struckThrough />
      </Td>
      <Td verticalAlign="top">
        <OrganizationTypeTag type={OrganizationType.Organization} />
      </Td>
      <Td verticalAlign="top" colSpan={2}>
        <Text role="alert" fontSize="sm" color="smError">
          {deadlineHasPassed
            ? t("Not added: the bid deadline has passed.")
            : t("Not added. Add them again to try once more.")}
        </Text>
      </Td>
    </Tr>
  );
};

const ORGANIZATION_INVITES_PATH = "privateContext.organizationInvites";
const INVITING_CLOSED = "zz_FieldErrorCode_InvitingClosedAfterBidDeadline";

const NotAddedRecipients = ({
  additions,
  deadlineHasPassed,
}: {
  additions: RecipientsAdded[];
  deadlineHasPassed: boolean;
}) => {
  const { t } = useTranslation();
  const labels = additions.flatMap(({ recipients }) => [
    ...recipients.personIds.map((id) => {
      const person = recipients.persons?.find((p) => p.id === id);
      return person ? `${person.firstName ?? ""} ${person.lastName ?? ""} (${person.email})`.trim() : id;
    }),
    ...recipients.emailInvites.map((emailInvite) => emailInvite.email),
  ]);
  return (
    <Box pt="1">
      {labels.map((label) => (
        <Text key={label} fontSize="sm" color="smMuted" textDecoration="line-through">
          {label}
        </Text>
      ))}
      <Text fontSize="sm" color="smError">
        {deadlineHasPassed
          ? t("Not added: the bid deadline has passed.")
          : t("Not added. Add them again to try once more.")}
      </Text>
    </Box>
  );
};

const withoutRecipients = (
  invite: OrganizationInvitePatchDto,
  additions: RecipientsAdded[],
): OrganizationInvitePatchDto => {
  const refused = additions.filter((addition) => addition.organizationId === invite.organizationId);
  if (refused.length === 0) return invite;
  const refusedPersonIds = new Set(refused.flatMap(({ recipients }) => recipients.personIds));
  const refusedEmails = new Set(
    refused.flatMap(({ recipients }) => recipients.emailInvites.map((e) => e.email.trim().toLowerCase())),
  );
  return {
    ...invite,
    persons: (invite.persons ?? []).filter((person) => !refusedPersonIds.has(person.id)),
    emailInvites: (invite.emailInvites ?? []).filter((e) => !refusedEmails.has(e.email.trim().toLowerCase())),
  };
};
